import type { AdminCompanyAdminStatus, AdminUserRow } from '../../../services/api';
import { getAdminUserAuth0SyncState } from '../../../lib/admin-user-auth0-syncability';
import { formatAdminUserLastLogin } from '../../../lib/admin-user-last-login';
import { AdminDashboardPaginatedSection } from '../AdminDashboardPrimitives';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';

const COMPANY_ADMIN_LOADING_REASON = 'Company admin statuses are still loading.';
const COMPANY_ADMIN_ERROR_REASON = 'Company admin statuses could not be loaded.';
const COMPANY_ADMIN_MISSING_REASON = 'Company admin status is unavailable for this user.';
const COMPANY_ADMIN_INACTIVE_REASON = 'User is inactive.';
const COMPANY_ADMIN_UNSYNCED_REASON =
    'User must be synced to Auth0 before company admin access can be changed.';
const COMPANY_ADMIN_NO_ACTIVE_MEMBERSHIP_REASON =
    'User must have an active membership on an active team in this company.';
const COMPANY_ADMIN_PENDING_REASON = 'Updating company admin access.';

function formatUserCompanyLabels(user: AdminUserRow): string {
    if (user.memberships.length === 0) {
        return user.companyNames.join(', ') || '—';
    }
    const labels = new Map<number, string>();
    for (const membership of user.memberships) {
        const name = membership.companyName.trim() || 'Unnamed company';
        labels.set(membership.companyId, `${name} (#${membership.companyId})`);
    }
    return [...labels.values()].join(', ') || '—';
}

function formatUserTeamLabels(user: AdminUserRow): string {
    if (user.memberships.length === 0) {
        return user.teamNames.join(', ') || '—';
    }
    return (
        user.memberships
            .map((membership) => {
                const name = membership.teamName.trim() || 'Unnamed team';
                return `${name} (#${membership.teamId})`;
            })
            .join(', ') || '—'
    );
}

function StatusIcon({ value }: { value: boolean }) {
    return <span aria-label={value ? 'Yes' : 'No'}>{value ? '✅' : '❌'}</span>;
}

function getCompanyAdminDisabledReason({
    user,
    status,
    isCompanyAdminStatusPending,
    isCompanyAdminStatusError,
    activeCompanyMemberUserIds,
    isPendingUser,
}: {
    user: AdminUserRow;
    status: AdminCompanyAdminStatus | undefined;
    isCompanyAdminStatusPending: boolean;
    isCompanyAdminStatusError: boolean;
    activeCompanyMemberUserIds: Set<number>;
    isPendingUser: boolean;
}): string | null {
    if (isPendingUser) {
        return COMPANY_ADMIN_PENDING_REASON;
    }
    if (isCompanyAdminStatusPending) {
        return COMPANY_ADMIN_LOADING_REASON;
    }
    if (isCompanyAdminStatusError) {
        return COMPANY_ADMIN_ERROR_REASON;
    }
    if (!status) {
        return COMPANY_ADMIN_MISSING_REASON;
    }
    if (!user.isActive) {
        return COMPANY_ADMIN_INACTIVE_REASON;
    }
    if (!user.isSyncedToAuth0) {
        return COMPANY_ADMIN_UNSYNCED_REASON;
    }
    if (!activeCompanyMemberUserIds.has(user.id)) {
        return COMPANY_ADMIN_NO_ACTIVE_MEMBERSHIP_REASON;
    }
    return null;
}

function CompanyAdminStatusText({
    status,
    isCompanyAdminStatusPending,
    isCompanyAdminStatusError,
}: {
    status: AdminCompanyAdminStatus | undefined;
    isCompanyAdminStatusPending: boolean;
    isCompanyAdminStatusError: boolean;
}) {
    if (isCompanyAdminStatusPending) {
        return <span className="text-neutral-500">Loading admin status...</span>;
    }
    if (isCompanyAdminStatusError || !status) {
        return <span className="text-neutral-500">Admin status unavailable</span>;
    }
    return <span>{status.isAdmin ? 'Admin' : 'Not admin'}</span>;
}

export function CompanyUsersPaginatedSection({
    rows,
    page,
    pageSize,
    total,
    totalPages,
    onPageChange,
    onRowClick,
    onSync,
    syncingUserId,
    companyAdminStatusByUserId,
    isCompanyAdminStatusPending,
    isCompanyAdminStatusError,
    activeCompanyMemberUserIds,
    companyAdminPendingUserId,
    onSetCompanyUserAdmin,
}: {
    rows: AdminUserRow[];
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onRowClick: (user: AdminUserRow) => void;
    onSync: (userId: number) => void;
    syncingUserId: number | null;
    companyAdminStatusByUserId: Map<number, AdminCompanyAdminStatus>;
    isCompanyAdminStatusPending: boolean;
    isCompanyAdminStatusError: boolean;
    activeCompanyMemberUserIds: Set<number>;
    companyAdminPendingUserId: number | null;
    onSetCompanyUserAdmin: (userId: number, isAdmin: boolean) => void;
}) {
    return (
        <AdminDashboardPaginatedSection
            rows={rows}
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPageChange={onPageChange}
            render={(pageRows) => (
                <Card className="overflow-hidden">
                    <Table className="min-w-[1280px]">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Companies</TableHead>
                                <TableHead>Teams</TableHead>
                                <TableHead>SSO</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Last login</TableHead>
                                <TableHead>Auth0</TableHead>
                                <TableHead>Company admin</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {pageRows.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={9}
                                        className="text-center text-sm text-neutral-600"
                                    >
                                        No users on teams for this company yet.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                pageRows.map((user) => {
                                    const syncState = getAdminUserAuth0SyncState(user);
                                    const companyAdminStatus = companyAdminStatusByUserId.get(
                                        user.id,
                                    );
                                    const companyAdminPending =
                                        companyAdminPendingUserId === user.id;
                                    const companyAdminDisabledReason =
                                        getCompanyAdminDisabledReason({
                                            user,
                                            status: companyAdminStatus,
                                            isCompanyAdminStatusPending,
                                            isCompanyAdminStatusError,
                                            activeCompanyMemberUserIds,
                                            isPendingUser: companyAdminPending,
                                        });
                                    const companyAdminDisabled =
                                        companyAdminDisabledReason !== null;
                                    const nextCompanyAdminState = !companyAdminStatus?.isAdmin;
                                    const companyAdminActionLabel = companyAdminPending
                                        ? 'Updating company admin'
                                        : companyAdminStatus?.isAdmin
                                          ? 'Remove company admin'
                                          : 'Grant company admin';

                                    return (
                                        <TableRow
                                            key={user.id}
                                            className="cursor-pointer"
                                            onClick={() => onRowClick(user)}
                                        >
                                            <TableCell className="font-medium text-neutral-950">
                                                {user.name}
                                            </TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>{formatUserCompanyLabels(user)}</TableCell>
                                            <TableCell>{formatUserTeamLabels(user)}</TableCell>
                                            <TableCell>{user.isSso ? 'Yes' : 'No'}</TableCell>
                                            <TableCell>
                                                <StatusIcon value={user.isActive} />
                                            </TableCell>
                                            <TableCell className="text-sm text-neutral-700">
                                                {formatAdminUserLastLogin(user)}
                                            </TableCell>
                                            <TableCell>
                                                {user.isSyncedToAuth0 ? (
                                                    <StatusIcon value />
                                                ) : (
                                                    <span className="inline-flex items-center gap-2">
                                                        <StatusIcon value={false} />
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            disabled={
                                                                syncingUserId === user.id ||
                                                                !syncState.canSync
                                                            }
                                                            title={
                                                                !syncState.canSync
                                                                    ? (syncState.disabledReason ??
                                                                      undefined)
                                                                    : undefined
                                                            }
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                onSync(user.id);
                                                            }}
                                                        >
                                                            {syncingUserId === user.id
                                                                ? 'Syncing...'
                                                                : 'Sync'}
                                                        </Button>
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div
                                                    className="flex flex-col items-start gap-1.5"
                                                    onClick={(event) => event.stopPropagation()}
                                                    onKeyDown={(event) => event.stopPropagation()}
                                                >
                                                    <CompanyAdminStatusText
                                                        status={companyAdminStatus}
                                                        isCompanyAdminStatusPending={
                                                            isCompanyAdminStatusPending
                                                        }
                                                        isCompanyAdminStatusError={
                                                            isCompanyAdminStatusError
                                                        }
                                                    />
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        disabled={companyAdminDisabled}
                                                        title={
                                                            companyAdminDisabledReason ?? undefined
                                                        }
                                                        aria-label={companyAdminActionLabel}
                                                        onClick={() => {
                                                            if (companyAdminDisabled) {
                                                                return;
                                                            }
                                                            if (!nextCompanyAdminState) {
                                                                const confirmed = window.confirm(
                                                                    `Remove company admin access for ${user.name}? Every active team role for this user in this company will reset to Basic.`,
                                                                );
                                                                if (!confirmed) {
                                                                    return;
                                                                }
                                                            }
                                                            onSetCompanyUserAdmin(
                                                                user.id,
                                                                nextCompanyAdminState,
                                                            );
                                                        }}
                                                    >
                                                        {companyAdminPending
                                                            ? 'Updating...'
                                                            : companyAdminStatus?.isAdmin
                                                              ? 'Remove'
                                                              : 'Grant'}
                                                    </Button>
                                                    {companyAdminDisabledReason ? (
                                                        <span className="text-xs text-neutral-500">
                                                            {companyAdminDisabledReason}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </Card>
            )}
        />
    );
}
